import React, { useState } from 'react';
import { useCart } from 'react-use-cart';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../../Contexts/NotificationContext';
import { GlowingEffect } from '../../../Shared/GlowingEffect/GlowingEffect';
import '../../../Assets/Stylesheets/Pages/MarketingServices/Socials.scss';

// Import all the platform logos
import youtubeLogo from '../../../Assets/Images/Youtube_logo.png';
import facebookLogo from '../../../Assets/Images/Facebook_logo.png';
import instagramLogo from '../../../Assets/Images/Instagram_logo.png';
import tiktokLogo from '../../../Assets/Images/Tiktok_logo.png';
import xLogo from '../../../Assets/Images/X_logo.png';
import spotifyLogo from '../../../Assets/Images/Spotify_logo.png';
import snapchatLogo from '../../../Assets/Images/Snapchat_logo.png';

// Array to hold the platforms' data
const platforms = [
  { id: 'youtube', name: 'YouTube', logo: youtubeLogo },
  { id: 'facebook', name: 'Facebook', logo: facebookLogo },
  { id: 'instagram', name: 'Instagram', logo: instagramLogo },
  { id: 'tiktok', name: 'TikTok', logo: tiktokLogo },
  { id: 'x', name: 'X', logo: xLogo },
  { id: 'spotify', name: 'Spotify', logo: spotifyLogo },
  { id: 'snapchat', name: 'Snapchat', logo: snapchatLogo },
];

// For the plans
const platformLogos = {
  youtube: youtubeLogo,
  facebook: facebookLogo,
  instagram: instagramLogo,
  tiktok: tiktokLogo,
  x: xLogo,
  spotify: spotifyLogo,
  snapchat: snapchatLogo,
};

const prebuiltPlans = {
  starter: 799.99,
  growth: 1999.99,
  pro: 4999.99,
};

const planFeatures = {
  starter: ["5,000 Followers", "50,000 Post Engagement", "2,000 Likes", "Basic Analytics"],
  growth: ["15,000 Followers", "200,000 Post Engagement", "10,000 Likes", "500 Comments", "30 Min Monthly Call"],
  pro: ["50,000 Followers", "1M Post Engagement", "50,000 Likes", "5,000 Comments", "1HR Monthly Call", "Priority Support"],
};

export default function OptionPrebuiltSocials() {
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [selectedPlanPrice, setSelectedPlanPrice] = useState(0);
  const [serviceFees, setServiceFees] = useState(0);
  const [isPlatformCollapsed, setIsPlatformCollapsed] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");
  
  const { addItem } = useCart();
  const navigate = useNavigate();
  const { showSuccess } = useNotification();

  const togglePlatformCollapse = () => {
    setIsPlatformCollapsed(!isPlatformCollapsed);
  };

  const handlePlatformSelection = (platformId) => {
    setSelectedPlatform(platformId);
    setSelectedPlanPrice(0);
    setServiceFees(0);
    setSelectedPlan("");
    setIsPlatformCollapsed(true);
  };

  const formatCurrency = (number) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(number);
  };

  const handlePlanSelection = (plan) => {
    const planPrice = prebuiltPlans[plan];
    setSelectedPlanPrice(planPrice);
    setSelectedPlan(plan);
    // Calculate 15% service fee
    setServiceFees(planPrice * 0.15);
    setIsPlatformCollapsed(true);
  };

  const subTotal = selectedPlanPrice + serviceFees;

  const handleAddToCart = () => {
    const item = {
      id: `social-growth-${selectedPlatform}-${selectedPlan}-${Date.now()}`,
      name: `${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} Plan - Social Media Growth`,
      platform: selectedPlatform.charAt(0).toUpperCase() + selectedPlatform.slice(1),
      price: subTotal,
      image: platformLogos[selectedPlatform],
      description: `Growth Budget: ${formatCurrency(selectedPlanPrice)} + Service Fees: ${formatCurrency(serviceFees)} | ${planFeatures[selectedPlan].join(', ')}`,
      type: 'social-media-growth'
    };
    addItem(item);
    showSuccess('Item added to cart!');
  };

  const handleBuyNow = () => {
    handleAddToCart();
    navigate('/checkout');
  };

  // Custom platform component
  const PlatformSelector = ({ platform }) => {
    const isSelected = selectedPlatform === platform.id;
    
    return (
      <div 
        className={`sops-platform-item ${isSelected ? 'selected-platform' : ''}`}
        onClick={() => handlePlatformSelection(platform.id)}
      >
        <div className="sops-platform-wrapper">
          <div className="sops-platform-container">
            {isSelected ? (
              <div className="sops-static-glow"></div>
            ) : (
              <GlowingEffect
                spread={15}
                glow={true}
                disabled={false}
                proximity={60}
                inactiveZone={0}
                borderWidth={0}
                className="sops-centered-glow"
              />
            )}
            <img 
              src={platform.logo} 
              alt={`${platform.name} Logo`} 
              className="sops-platform-logo" 
            />
          </div>
          <p className={isSelected ? 'sops-selected-name' : ''}>{platform.name}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="sops-option-prebuilt-container">
      <button onClick={togglePlatformCollapse} className="sops-collapse-toggle">
        {isPlatformCollapsed ? 'Change Selection' : 'Hide Platform Selection'}
      </button>
      
      {!isPlatformCollapsed && (
        <div className="sops-platform-grid-container">
          {platforms.map(platform => (
            <PlatformSelector key={platform.id} platform={platform} />
          ))}
        </div>
      )}

      <hr className="sops-section-divider" />

      {selectedPlatform && (
        <>
          <div className="sops-plan-section-container">
            <h2 className="sops-plan-section-title">Plan Selection</h2>
            <p className="sops-plan-section-description">
              Select the plan that best fits your social media growth needs. Each plan includes different levels of engagement and features.
            </p>
          </div>
          
          <div className="sops-prebuilt-plan-container">
            {Object.entries(prebuiltPlans).map(([plan, price]) => (
              <div 
                key={plan} 
                className={`sops-plan-card ${selectedPlan === plan ? "selected" : ""}`}
                onClick={() => handlePlanSelection(plan)}
              >
                <div className="sops-plan-card-header">
                  <div className="sops-platform-logo-wrapper">
                    <img src={platformLogos[selectedPlatform]} alt={`${selectedPlatform} logo`} className="sops-plan-platform-logo" />
                  </div>
                  <h3 className="sops-plan-title">{`${plan.charAt(0).toUpperCase() + plan.slice(1)} Plan`}</h3>
                </div>
                
                <div className="sops-plan-price-section">
                  <span className="sops-price-label">Starting at</span>
                  <div className="sops-price-amount">{formatCurrency(price)}</div>
                </div>

                <div className="sops-plan-features-list">
                  {planFeatures[plan].map((feature, index) => (
                    <div key={index} className="sops-feature-item">
                      <span className="sops-feature-check">✓</span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <button 
                  className="sops-select-plan-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlanSelection(plan);
                  }}
                >
                  {selectedPlan === plan ? 'Selected' : 'Select Plan'}
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {selectedPlanPrice > 0 && (
        <div className="sops-budget-summary-container">
          <div className="total-budget-container">
            <span className="budget-label">Social Growth Budget:</span>
            <span className="budget-value">{formatCurrency(selectedPlanPrice)}</span>
          </div>
          <div className="media-planning-fees-container">
            <span className="budget-label">Service Fees:</span>
            <span className="budget-value">{formatCurrency(serviceFees)}</span>
          </div>
          <div className="subtotal-container">
            <span className="budget-label">Sub-Total:</span>
            <span className="budget-value">{formatCurrency(subTotal)}</span>
          </div>
          <div className="pads-action-buttons-container">
            <button className="pads-add-to-cart" onClick={handleAddToCart}>Add to Cart</button>
            <button className="pads-buy-now" onClick={handleBuyNow}>Buy Now</button>
          </div>
        </div>
      )}
    </div>
  );
}
